import axios from "axios";
import { jwtDecode } from "jwt-decode";

const API_URL = process.env.REACT_APP_API_URL || "https://api.apexacademiccentre.co.za/api";

const AUTH_KEYS = {
  token: 'token',
  role: 'role',
  user: 'user',
  expiresAt: 'tokenExpiresAt',
  remember: 'rememberMe',
};

const getStorage = (type) => {
  if (typeof window === 'undefined') return null;
  try {
    return type === 'local' ? window.localStorage : window.sessionStorage;
  } catch (err) {
    console.warn('Storage unavailable', err);
    return null;
  }
};

const readAuthValue = (key) => {
  const session = getStorage('session');
  if (session) {
    const value = session.getItem(key);
    if (value !== null && value !== undefined) return value;
  }
  const local = getStorage('local');
  return local ? local.getItem(key) : null;
};

const writeToStore = (store, key, value) => {
  if (!store) return;
  if (value === undefined || value === null) {
    store.removeItem(key);
  } else {
    store.setItem(key, value);
  }
};

const purgeStore = (store) => {
  if (!store) return;
  Object.values(AUTH_KEYS).forEach((key) => {
    try {
      store.removeItem(key);
    } catch (err) {
      console.warn('Failed to clear auth key', key, err);
    }
  });
};

export const clearStoredAuth = () => {
  purgeStore(getStorage('session'));
  purgeStore(getStorage('local'));
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new Event('auth:cleared'));
    } catch (err) {
      console.warn('Failed to dispatch auth clear event', err);
    }
  }
};

const persistAuth = ({ token, role, user, remember, tokenExpiresAt }) => {
  const session = getStorage('session');
  const local = getStorage('local');
  const primary = remember ? local : session;
  const secondary = remember ? session : local;

  const userPayload = user ? JSON.stringify(user) : null;

  writeToStore(primary, AUTH_KEYS.token, token || null);
  writeToStore(primary, AUTH_KEYS.role, role || null);
  writeToStore(primary, AUTH_KEYS.user, userPayload);
  writeToStore(primary, AUTH_KEYS.expiresAt, tokenExpiresAt ? String(tokenExpiresAt) : null);
  writeToStore(primary, AUTH_KEYS.remember, remember ? 'true' : 'false');

  purgeStore(secondary);
};

const decodeExpiry = (token) => {
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    if (!exp) return null;
    const millis = exp * 1000;
    return Number.isFinite(millis) ? millis : null;
  } catch (err) {
    return null;
  }
};

export const login = async (email, password, rememberMe = false) => {
  try {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password, rememberMe });
    const { token, role, user, tokenExpiresAt } = response.data || {};

    const resolvedExpiry = tokenExpiresAt ?? decodeExpiry(token);

    try {
      persistAuth({ token, role, user, remember: !!rememberMe, tokenExpiresAt: resolvedExpiry });
    } catch (storageErr) {
      console.warn('Auth storage unavailable', storageErr);
    }

    return { token, role, user, tokenExpiresAt: resolvedExpiry };
  } catch (error) {
    console.error('Error logging in', error);
    throw new Error(error?.response?.data?.message || 'Invalid credentials');
  }
};

export const getToken = () => {
  try {
    return readAuthValue(AUTH_KEYS.token);
  } catch {
    return null;
  }
};

export const getUser = () => {
  try {
    const raw = readAuthValue(AUTH_KEYS.user);
    if (!raw || raw === 'undefined') return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const getRole = () => {
  try {
    return readAuthValue(AUTH_KEYS.role);
  } catch {
    return null;
  }
};

export const getTokenExpiry = () => {
  try {
    const raw = readAuthValue(AUTH_KEYS.expiresAt);
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
};

export const getRememberFlag = () => {
  try {
    return readAuthValue(AUTH_KEYS.remember) === 'true';
  } catch {
    return false;
  }
};

export const logout = (options = { redirect: true }) => {
  try {
    clearStoredAuth();
  } catch {
    // ignore
  }

  if (options.redirect !== false && typeof window !== 'undefined') {
    window.location.href = '/login';
  }
};

export const isTokenExpired = () => {
  const storedExpiry = getTokenExpiry();
  if (storedExpiry) {
    return storedExpiry <= Date.now();
  }

  const token = getToken();
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    if (!exp) return false;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

